
// src/firebase.ts
import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Securely validate the configuration
if (!firebaseConfig.projectId || !firebaseConfig.appId) {
  throw new Error("Firebase configuration is missing or incomplete. Please check your client-side environment variables (VITE_FIREBASE_PROJECT_ID, etc).");
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
// Set authentication persistence to sessionStorage.
// This ensures that each browser tab has its own isolated user session.
setPersistence(auth, browserSessionPersistence);


const databaseId = import.meta.env.VITE_FIRESTORE_DB;
const db = getFirestore(app, databaseId || undefined);

// Ensure region is set, otherwise fail fast. Defaulting to us-central1 causes CORS issues in production.
const region = import.meta.env.VITE_GCP_REGION?.trim();
if (!region) {
  throw new Error("VITE_GCP_REGION is not defined. Firebase functions calls will fail. Check your build-time environment variables.");
}

console.log(`[Firebase] Initializing Functions with region: ${region}`);
const functions = getFunctions(app, region);
if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
  console.log('[Firebase] Connecting to Functions emulator at 127.0.0.1:5001');
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
const storage = getStorage(app);

// Expose functions to the window for the temporary admin grant
(window as any).firebaseFunctions = functions;

export { app, auth, db, functions, storage };
